import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
	Button,
	Icon,
	SettingsGroup,
	SettingsItemAutocomplete,
	SettingsItemCheckbox,
	SettingsItemTextField,
	Tooltip,
	useAutocomplete,
	useTable,
} from '@elipssolution/harfang';
import { mdiAlertCircle, mdiEyeOutline, mdiPencil, mdiPlus } from '@mdi/js';
import { Stack, Typography, styled } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import CustomerFilesByUserTable from './CustomerFilesByUserTable';
import InternalUserPermissionModules from './InternalUserPermissionModules';
import { useConfirmationDialogContext } from '../../../../components/ConfirmationDialogProvider';
import SettingsDialogPage from '../../../../components/SettingsDialogPage';
import { useSettingsDialog } from '../../../../hooks/useSettingsDialog';
import { PermissionEnum } from '../../../../types/permission';
import { generateErrorInformations } from '../../../../utils/errorHandler';
import { renderFullName } from '../../../../utils/renderFullName';
import { SUCCESS_ANIMATION_DELAY } from '../../../../utils/statusAnimationDelay';
import {
	FETCH_INTERNAL_GROUPS,
	FETCH_INTERNAL_GROUP_BY_DEFAULT,
	FetchInternalGroupByDefaultType,
	FetchInternalGroupsType,
} from '../../../api/internalGroup';
import {
	FETCH_INTERNAL_USER,
	FetchInternalUserType,
	REMOVE_INTERNAL_USER,
	RemoveInternalUserType,
	UPDATE_INTERNAL_USER,
	UpdateInternalUserType,
} from '../../../api/internalUser';
import { useSession } from '../../../components/session/SessionContext';
import { GroupType } from '../../../types/group';
import {
	AddInternalUserRelationWithCustomerFilesType,
	AddInternalUserType,
	InternalUserFormType,
	InternalUserType,
} from '../../../types/user';
import SettingsItemEmailField from '../../SettingsItemEmailField';
import { useUserCustomerFileRelationContext } from '../../UserCustomerFileRelationProvider';

const ErrorWrapper = styled('div')(({ theme: { spacing, palette, shape } }) => ({
	overflow: 'auto',
	minHeight: 100,
	minWidth: '50%',
	maxWidth: '75%',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	padding: spacing(1),
	gap: spacing(1),
	margin: '16px auto 0 auto',
	color: palette.error.main,
	backgroundColor: `${palette.error.main}1A`,
	borderRadius: shape.borderRadius,
}));

type InternalUserFormProps = {
	user: InternalUserType;
};

const InternalUserForm = ({ user: initialUser }: InternalUserFormProps) => {
	const { back, onHasDirtyFormChange, onCloseConfirmationTitleChange } = useSettingsDialog();
	const { user: sessionUser, refetch: refetchSession, checkPermission, impersonatedUser, impersonate } = useSession();
	const { userCustomerFileRelations, setUserCustomerFileRelations } = useUserCustomerFileRelationContext();
	const { confirmAction } = useConfirmationDialogContext();

	const customerFilesByUserTableInstance = useTable();
	const groupAutocompleteInstance = useAutocomplete();

	const {
		control,
		formState: { isDirty, isValid, dirtyFields },
		handleSubmit,
		reset,
	} = useForm<InternalUserFormType>({
		mode: 'onChange',
	});

	const [user, setUser] = useState(initialUser);
	const [errorMessage, setErrorMessage] = useState<string>();

	const { loading: isInternalUserLoading, refetch: refetchInternalUser } = useQuery<FetchInternalUserType>(
		FETCH_INTERNAL_USER,
		{
			variables: { internalUserId: initialUser.id },
			onCompleted: ({ internalUser }) => setUser(internalUser),
		},
	);

	const { data: internalGroupByDefaultData, loading: isInternalGroupByDefaultLoading } =
		useQuery<FetchInternalGroupByDefaultType>(FETCH_INTERNAL_GROUP_BY_DEFAULT);

	const [
		updateInternalUser,
		{
			data: updateInternalUserData,
			loading: isUpdateInternalUserLoading,
			reset: resetUpdateInternalUserMutationStatus,
		},
	] = useMutation<UpdateInternalUserType>(UPDATE_INTERNAL_USER, {
		onCompleted: ({ updateInternalUser: updatedInternalUser }) => {
			setUser(updatedInternalUser);
			if (dirtyFields.group) {
				customerFilesByUserTableInstance.reload();
			}

			if (userCustomerFileRelations.length > 0) {
				const {
					email,
					firstName,
					group,
					hasUserPermissions,
					id,
					lastName,
					customerFiles,
					permissionModules,
					isBillingManager,
				} = updatedInternalUser;

				setUserCustomerFileRelations((prevValues) => {
					if ('user' in prevValues[0]) {
						return (prevValues as AddInternalUserRelationWithCustomerFilesType[]).map(
							({ user: prevUser, customerFiles: prevCustomerFiles }) =>
								prevUser.id === id
									? {
											user: {
												email,
												firstName,
												group,
												hasUserPermissions,
												id,
												lastName,
												customerFiles,
												permissionModules,
												isBillingManager,
											},
											customerFiles: prevCustomerFiles,
									  }
									: { user: prevUser, customerFiles: prevCustomerFiles },
						);
					}

					return [
						...(prevValues as AddInternalUserType[]).filter(({ id: prevUserId }) => id !== prevUserId),
						{
							email,
							firstName,
							group,
							hasUserPermissions,
							id,
							lastName,
							customerFiles,
							permissionModules,
							isBillingManager,
						},
					];
				});
			}

			setTimeout(() => resetUpdateInternalUserMutationStatus(), SUCCESS_ANIMATION_DELAY);
		},
		onError: (error) => {
			setErrorMessage(generateErrorInformations({ error, resource: 'updateInternalUser' }).message);
		},
	});

	const [removeInternalUser, { loading: isRemoveInternalUserLoading }] = useMutation<RemoveInternalUserType>(
		REMOVE_INTERNAL_USER,
		{
			onCompleted: ({ removeInternalUser: { id } }) => {
				if (userCustomerFileRelations.length > 0) {
					setUserCustomerFileRelations((prevValues) => {
						if ('user' in prevValues[0]) {
							return (prevValues as AddInternalUserRelationWithCustomerFilesType[]).filter(
								({ user: prevUser }) => prevUser.id !== id,
							);
						}

						return (prevValues as AddInternalUserType[]).filter(({ id: prevUserId }) => id !== prevUserId);
					});
				}

				back();
			},
			onError: (err) => {
				if (err.message.includes('violates foreign key constraint') && err.message.includes('brochure')) {
					setErrorMessage('Impossible de supprimer cet utilisateur, car des plaquettes lui sont associées.');
				}
			},
		},
	);

	const [fetchInternalGroups, { loading: isInternalGroupsLoading }] =
		useLazyQuery<FetchInternalGroupsType>(FETCH_INTERNAL_GROUPS);

	const internalGroupDataSource = useCallback(
		async (limit: number, offset: number, search?: string) => {
			const { data, error } = await fetchInternalGroups({
				variables: {
					page: {
						limit,
						offset,
					},
					search,
				},
			});

			if (error) {
				throw error;
			}

			if (!data) {
				throw new Error('Aucune donnée reçue');
			}

			return data.internalGroups;
		},
		[fetchInternalGroups],
	);

	const isLoading = useMemo(
		() =>
			isInternalUserLoading ||
			isInternalGroupsLoading ||
			isInternalGroupByDefaultLoading ||
			isUpdateInternalUserLoading ||
			isRemoveInternalUserLoading,
		[
			isInternalGroupByDefaultLoading,
			isInternalGroupsLoading,
			isInternalUserLoading,
			isRemoveInternalUserLoading,
			isUpdateInternalUserLoading,
		],
	);

	const isMutationButtonDisabled = useMemo(() => isLoading || !isValid || !isDirty, [isDirty, isLoading, isValid]);

	const {
		lastName,
		firstName,
		group = internalGroupByDefaultData?.internalGroupByDefault,
		permissionModules: permissionModulesQuery = [],
	} = user ?? {};

	const isAdministrator = !!group?.isAdministrator;

	const isUserSelectedUserConnected = user.id === sessionUser.id;

	const permissionModules = useMemo(
		() =>
			permissionModulesQuery.map(({ permissions, ...permissionModuleRest }) => ({
				...permissionModuleRest,
				permissions: permissions.map(({ value, inheritedValue, ...permissionRest }) => ({
					...permissionRest,
					inheritedValue: isAdministrator || inheritedValue,
					value: isAdministrator || value,
				})),
			})),
		[isAdministrator, permissionModulesQuery],
	);

	const submitButtonLabel = useMemo(() => {
		if (user) {
			if (updateInternalUserData) return 'Utilisateur modifié';

			return 'Modifier';
		}

		return 'Ajouter';
	}, [updateInternalUserData, user]);

	const onSubmit = useCallback(
		({
			email: formEmail,
			lastName: formLastName,
			firstName: formFirstName,
			group: formGroup,
			isBillingManager: formIsBillingManager,
		}: InternalUserFormType) =>
			updateInternalUser?.({
				variables: {
					updateInternalUserInput: {
						id: user.id,
						email: formEmail,
						lastName: formLastName,
						firstName: formFirstName || null,
						groupId: formGroup?.id,
						isBillingManager: formIsBillingManager,
					},
				},
			}),
		[updateInternalUser, user.id],
	);

	const handleSubmitClick = useCallback(async () => {
		if (user.id && dirtyFields.email) {
			const isEmailModificationConfirmed = await confirmAction({
				title: `Êtes-vous sûr de vouloir modifier l'utilisateur ${renderFullName({ firstName, lastName })} ?`,
				content: `Vous avez modifié l'adresse mail de l'utilisateur.
				Cette modification va également modifier l'identifiant de connexion de l'utilisateur ce qui pourrait l'empêcher de se connecter.

				Assurez-vous d'avoir communiqué cette modification avant de confirmer.`,
				confirmButtonDetails: { label: 'Confirmer la modification' },
			});

			if (!isEmailModificationConfirmed) {
				return null;
			}
		}

		return handleSubmit(onSubmit)();
	}, [user.id, dirtyFields.email, handleSubmit, onSubmit, confirmAction, firstName, lastName]);

	const handleDeleteClick = async () => {
		const isDeleteConfirmed = await confirmAction({
			title: `Êtes-vous sûr de vouloir supprimer l'utilisateur ${renderFullName({
				firstName,
				lastName,
			})} ?`,
			confirmButtonDetails: { color: 'error', label: 'Supprimer' },
		});

		return isDeleteConfirmed
			? removeInternalUser({
					variables: {
						removeInternalUserId: user.id,
					},
			  })
			: null;
	};

	const handlePermissionChange = useCallback(() => {
		refetchSession().catch((e) => {
			throw e;
		});

		refetchInternalUser().catch((e) => {
			throw e;
		});
	}, [refetchInternalUser, refetchSession]);

	useEffect(
		() =>
			reset({
				...user,
				group,
			}),
		[reset, user, group],
	);

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
		onCloseConfirmationTitleChange("Êtes-vous sûr de vouloir quitter la modification de l'utilisateur ?");
	}, [isDirty, onCloseConfirmationTitleChange, onHasDirtyFormChange, user]);

	return (
		<SettingsDialogPage
			title={renderFullName({ firstName, lastName })}
			titleActionButton={
				user &&
				!impersonatedUser &&
				checkPermission(PermissionEnum.IMPERSONATE) &&
				!isUserSelectedUserConnected &&
				!isAdministrator && (
					<Button
						variant="outlined"
						color="inherit"
						onClick={() => impersonate(user)}
						startIcon={<Icon path={mdiEyeOutline} />}
					>
						Voir en tant que
					</Button>
				)
			}
		>
			<SettingsGroup>
				<Controller
					name="lastName"
					control={control}
					render={({ field: { onChange, ...field } }) => {
						const handleUpperCaseChange = (value?: string) => {
							onChange(value?.toUpperCase());
						};

						return (
							<SettingsItemTextField
								{...field}
								description="Nom de l'utilisateur."
								disabled={isInternalUserLoading}
								label="Nom"
								onChange={handleUpperCaseChange}
								required
							/>
						);
					}}
					rules={{ required: true }}
				/>
				<Controller
					name="firstName"
					control={control}
					render={({ field: { onChange, ...field } }) => {
						const handleFirstLetterUpperCaseChange = (value?: string) => {
							onChange(value ? value.charAt(0).toUpperCase() + value.slice(1) : undefined);
						};

						return (
							<SettingsItemTextField
								{...field}
								description="Prénom de l'utilisateur."
								disabled={isInternalUserLoading}
								label="Prénom"
								onChange={handleFirstLetterUpperCaseChange}
							/>
						);
					}}
				/>
				<SettingsItemEmailField<InternalUserFormType>
					control={control}
					disabled={isInternalUserLoading}
					initialValue={user.email}
				/>
				<Controller
					name="group"
					control={control}
					render={({ field }) => (
						<SettingsItemAutocomplete<GroupType>
							{...field}
							autocomplete={groupAutocompleteInstance}
							dataSource={internalGroupDataSource}
							description="Groupe attaché à l'utilisateur."
							disabled={isUserSelectedUserConnected || isInternalUserLoading}
							getOptionLabel={(option) => option.name}
							label="Groupe"
							disableClearable
							required
						/>
					)}
					rules={{ required: true }}
				/>
				<Controller
					name="isBillingManager"
					defaultValue={false}
					control={control}
					render={({ field: { value, ...field } }) => (
						<SettingsItemCheckbox {...field} checked={value} label="Est responsable facturation" />
					)}
				/>
			</SettingsGroup>

			<Stack flexDirection="row-reverse" justifyContent="space-between">
				<Button
					disabled={isInternalUserLoading || isMutationButtonDisabled}
					onClick={handleSubmitClick}
					startIcon={<Icon path={user ? mdiPencil : mdiPlus} />}
					variant="contained"
				>
					{submitButtonLabel}
				</Button>

				{errorMessage && (
					<ErrorWrapper>
						<Icon path={mdiAlertCircle} />
						<Typography>{errorMessage}</Typography>
					</ErrorWrapper>
				)}

				{lastName && (
					<Tooltip
						content={isUserSelectedUserConnected ? 'Un utilisateur ne peut pas se supprimer lui même' : undefined}
					>
						<Button
							disabled={isLoading || isUserSelectedUserConnected}
							onClick={handleDeleteClick}
							variant="outlined"
							color="error"
						>
							Supprimer
						</Button>
					</Tooltip>
				)}
			</Stack>

			<CustomerFilesByUserTable
				isAdministrator={isAdministrator}
				user={user}
				tableInstance={customerFilesByUserTableInstance}
			/>

			<InternalUserPermissionModules
				disabled={isAdministrator || isUserSelectedUserConnected}
				loading={isInternalUserLoading}
				permissionModules={permissionModules}
				onPermissionChanged={handlePermissionChange}
			/>
		</SettingsDialogPage>
	);
};

export default InternalUserForm;
